// Cluster merge overlay — Grant: "it says this is a 3-record cluster
// and to open it for full side-by-side. how do I open it?". The OS
// already ships ClusterMergeReviewOrchestrator: it drives the tested
// per-field MergeReviewWizard across N members (N=2 inline, N≥3
// pairwise) with a cluster-wide undo. This adapts a cleanup issue's
// affected contacts into the DuplicateCluster shape it expects and
// hands off — so 2-record AND 3+-record clusters open the same
// real side-by-side review. The orchestrator brings its own Modal;
// we don't wrap it.

import {
  type DuplicateContactSide,
  type DuplicateCluster,
} from "../api";
import { ClusterMergeReviewOrchestrator } from "../components/ClusterMergeReviewOrchestrator";
import type { RecordRef } from "./cleanup-records";

// Best-effort split of a display label into first/last for the
// (seed-skipped) pick card. The orchestrator fetches each member's
// full SafeContact by id, so these are cosmetic placeholders only.
function splitName(label: string): {
  first: string | null;
  last: string | null;
} {
  const parts = label.trim().split(/\s+/);
  if (parts.length === 0 || parts[0] === "") {
    return { first: null, last: null };
  }
  if (parts.length === 1) return { first: parts[0]!, last: null };
  return {
    first: parts.slice(0, -1).join(" "),
    last: parts[parts.length - 1]!,
  };
}

function toSide(ref: RecordRef): DuplicateContactSide {
  const { first, last } = splitName(ref.label);
  return {
    id: ref.id,
    legal_first_name: first,
    legal_last_name: last,
    preferred_name: null,
    email: null,
    phone_primary: null,
    household_id: null,
    household_display_name: null,
    legal_middle_name: null,
    legal_suffix: null,
    secondary_email: null,
    phone_secondary: null,
    street_line_1: null,
    street_line_2: null,
    city: null,
    state: null,
    zip: null,
    birthday: null,
    gender: null,
    marital_status: null,
    referral_source_type: null,
    referral_source_detail: null,
    real_estate_occupation: null,
    drivers_license_state: null,
    bank_account_type: null,
    transactional_ok: null,
    relational_ok: null,
    marketing_ok: null,
    do_not_email: null,
    sms_opt_in: null,
    tcpa_consent_at: null,
    what_matters_most: null,
    contact_created_at: null,
    contact_updated_at: null,
    household_primary_city: null,
    household_primary_state: null,
    household_primary_zip: null,
    household_member_count: null,
  };
}

export function ClusterMergeOverlay({
  members,
  matchType,
  matchValue,
  onClose,
  onMerged,
}: {
  members: RecordRef[];
  matchType: "email" | "phone" | "name_zip";
  matchValue: string;
  onClose: () => void;
  onMerged: () => void;
}) {
  const cluster: DuplicateCluster = {
    match_type: matchType,
    match_value: matchValue,
    members: members.map(toSide),
  };
  // Baker 2026-05-18 (PII regression): we previously seeded
  // members[0] as survivor for 2-record clusters (Grant's "skip the
  // picker for 2"). But affectedRecords order is arbitrary, so the
  // sparse record often won — sidelining the record that holds the
  // SSN / Spouse SSN / Co-borrower SSN, which is encryption-scoped
  // per-contact and PERMANENTLY LOST on merge. Baker could no longer
  // choose the SSN-bearing record as survivor.
  //
  // Fix: never pre-seed. The orchestrator runs its survivor-pick with
  // the suggestSurvivor default (most-complete record — i.e. the one
  // that actually carries the SSN/email/etc.) pre-selected, and the
  // user can still choose the other. For N=2 the orchestrator renders
  // that step inline (one click, smart default) — minimal friction,
  // and required for data integrity. (Tradeoff vs. Grant's
  // skip-for-2: PII safety wins; flagged to Grant.)
  return (
    <ClusterMergeReviewOrchestrator
      cluster={cluster}
      onClose={onClose}
      onComplete={onMerged}
    />
  );
}
